"use client";

import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Checkbox, InputNumber, Table, Typography, Empty, Spin } from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";
import {
  IConfigurableFormComponent,
  IToolboxComponent,
  DesignerToolbarSettings,
  validateConfigurableComponentSettings,
  evaluateString,
  useFormData,
  useGlobalState,
  useSheshaApplication,
  useForm,
} from "@shesha-io/reactjs";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AvailableLineItem {
  purchaseOrderLineItemId: string;
  lineItemNo: string;
  description: string;
  committedQuantity: number;
  committedUnitPrice: number;
  committedAmount: number;
  actualUnitPrice: number;
  remainingQuantity: number;
  remainingAmount: number;
}

interface SelectedLineItem extends AvailableLineItem {
  selected: boolean;
  actualQuantity: number | null;
  actualAmount: number;
  variance: number;
  fullyInvoiced: boolean;
}

interface IProps extends IConfigurableFormComponent {
  purchaseOrderId?: string;
}

/* ------------------------------------------------------------------ */
/*  Settings                                                           */
/* ------------------------------------------------------------------ */

const settingsMarkup = new DesignerToolbarSettings()
  .addContextPropertyAutocomplete({
    id: "prop-name",
    propertyName: "propertyName",
    parentId: "root",
    label: "Property Name",
    validate: { required: true },
  })
  .addTextField({
    id: "po-id",
    propertyName: "purchaseOrderId",
    parentId: "root",
    label: "Purchase Order ID",
    description:
      "Purchase order GUID, or a Mustache template such as {{data.order.id}} or {{globalState.selectedOrderId}}. Form data is exposed as `data` and global state as `globalState`. Leave empty to fall back to the order on the current form.",
  })
  .addCheckbox({
    id: "hidden-flag",
    propertyName: "hidden",
    parentId: "root",
    label: "Hidden",
  })
  .toJson();

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const fmt = (v: number | null | undefined) =>
  v == null
    ? "-"
    : v.toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

/* ------------------------------------------------------------------ */
/*  Table                                                              */
/* ------------------------------------------------------------------ */

const LineItemsTable: FC<{
  items: AvailableLineItem[];
  loading: boolean;
  hasOrderId: boolean;
  onSelectionChange: (selectedRows: SelectedLineItem[]) => void;
}> = ({ items, loading, hasOrderId, onSelectionChange }) => {
  const [rows, setRows] = useState<SelectedLineItem[]>([]);
  const { setState: setGlobalState } = useGlobalState();

  useEffect(() => {
    if (!items?.length) {
      setRows([]);
      return;
    }
    setRows(
      items.map((it) => {
        const committedAmount =
          (it.committedQuantity ?? 0) * (it.committedUnitPrice ?? 0);
        const fullyInvoiced = (it.remainingQuantity ?? 0) <= 0;

        if (fullyInvoiced) {
          const invoicedQuantity =
            (it.committedQuantity ?? 0) - (it.remainingQuantity ?? 0);
          const invoicedAmount = committedAmount - (it.remainingAmount ?? 0);
          const invoicedUnitPrice =
            invoicedQuantity > 0
              ? invoicedAmount / invoicedQuantity
              : it.actualUnitPrice ?? it.committedUnitPrice ?? 0;

          return {
            ...it,
            committedAmount,
            actualUnitPrice: invoicedUnitPrice,
            selected: false,
            actualQuantity: invoicedQuantity,
            actualAmount: invoicedAmount,
            variance: committedAmount - invoicedAmount,
            fullyInvoiced: true,
          };
        }

        return {
          ...it,
          committedAmount,
          actualUnitPrice: it.actualUnitPrice ?? it.committedUnitPrice ?? 0,
          selected: false,
          actualQuantity: null,
          actualAmount: 0,
          variance: 0,
          fullyInvoiced: false,
        };
      }),
    );
  }, [items]);

  useEffect(() => {
    onSelectionChange(rows.filter((r) => r.selected));
  }, [rows, onSelectionChange]);

  useEffect(() => {
    const hasIncorrectQuantity = rows.some(
      (r) =>
        r.selected &&
        ((r.actualQuantity !== null &&
          r.actualQuantity > (r.remainingQuantity ?? 0)) ||
          r.actualQuantity === 0 ||
          r.actualQuantity === null ||
          r.actualQuantity === undefined),
    );

    setGlobalState({ key: "hasIncorrectQuantity", data: hasIncorrectQuantity });
  }, [rows, setGlobalState]);

  const calc = useCallback((r: SelectedLineItem): SelectedLineItem => {
    const amt =
      r.selected && r.actualQuantity != null
        ? r.actualQuantity * r.actualUnitPrice
        : 0;
    return {
      ...r,
      actualAmount: amt,
      variance: r.selected ? r.committedAmount - amt : 0,
    };
  }, []);

  const toggle = useCallback(
    (id: string, on: boolean) =>
      setRows((prev) =>
        prev.map((r) =>
          r.purchaseOrderLineItemId === id ? calc({ ...r, selected: on }) : r,
        ),
      ),
    [calc],
  );

  const changeQty = useCallback(
    (id: string, qty: number | null) =>
      setRows((prev) =>
        prev.map((r) =>
          r.purchaseOrderLineItemId === id
            ? calc({ ...r, actualQuantity: qty })
            : r,
        ),
      ),
    [calc],
  );

  const changeUnitPrice = useCallback(
    (id: string, price: number | null) =>
      setRows((prev) =>
        prev.map((r) =>
          r.purchaseOrderLineItemId === id
            ? calc({ ...r, actualUnitPrice: price ?? 0 })
            : r,
        ),
      ),
    [calc],
  );

  const total = useMemo(
    () =>
      rows.filter((r) => r.selected).reduce((s, r) => s + r.actualAmount, 0),
    [rows],
  );

  useEffect(() => {
    setGlobalState({ key: "invoiceLineItemsTotal", data: total });
  }, [total, setGlobalState]);

  useEffect(() => {
    const hasNegativeVariance = rows.some(
      (r) => r.selected && r.variance < 0,
    );
    setGlobalState({ key: "hasNegativeVariance", data: hasNegativeVariance });
  }, [rows, setGlobalState]);

  // No order selected/evaluated yet — show the generic placeholder.
  if (!hasOrderId && !loading)
    return <Empty description="No line items available for this order" />;

  // Order is set but the API returned no items — show an empty table with a
  // contextual message rather than the generic Empty placeholder.
  return (
    <Spin spinning={loading}>
      <Table
        dataSource={rows}
        rowKey="purchaseOrderLineItemId"
        pagination={false}
        size="small"
        bordered
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "There are no line items for this order" }}
        columns={[
          {
            title: "Select",
            width: 70,
            align: "center" as const,
            render: (_: unknown, r: SelectedLineItem) => (
              <Checkbox
                checked={r.selected || r.fullyInvoiced}
                disabled={r.fullyInvoiced}
                onChange={(e) =>
                  toggle(r.purchaseOrderLineItemId, e.target.checked)
                }
              />
            ),
          },
          { title: "PO Item", dataIndex: "lineItemNo", width: 100 },
          { title: "Description", dataIndex: "description", ellipsis: true },
          {
            title: "Committed Item Qty",
            dataIndex: "committedQuantity",
            width: 140,
            align: "right" as const,
            render: fmt,
          },
          {
            title: "Committed Unit Price (Incl. VAT)",
            dataIndex: "committedUnitPrice",
            width: 200,
            align: "right" as const,
            render: fmt,
          },
          {
            title: "Committed Amount (Incl. VAT)",
            dataIndex: "committedAmount",
            width: 200,
            align: "right" as const,
            render: fmt,
          },
          {
            title: "Invoiced Quantity",
            width: 140,
            align: "right" as const,
            render: (_: unknown, r: SelectedLineItem) => (
              <>
                <div>
                  {fmt((r.committedQuantity ?? 0) - (r.remainingQuantity ?? 0))}
                </div>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11, display: "block", marginTop: 2 }}
                >
                  (Max. {fmt(r.committedQuantity)})
                </Typography.Text>
              </>
            ),
          },
          {
            title: "Actual Item Qty",
            width: 140,
            align: "right" as const,
            render: (_: unknown, r: SelectedLineItem) => {
              const exceedsRemaining =
                r.selected &&
                r.actualQuantity != null &&
                r.actualQuantity > (r.remainingQuantity ?? 0);
              return (
                <>
                  <InputNumber
                    value={r.actualQuantity}
                    disabled={!r.selected}
                    size="small"
                    status={exceedsRemaining ? "error" : undefined}
                    style={{ width: "100%" }}
                    onChange={(v) => changeQty(r.purchaseOrderLineItemId, v)}
                  />
                  {exceedsRemaining && (
                    <Typography.Text
                      type="danger"
                      style={{
                        fontSize: 11,
                        display: "block",
                        marginTop: 2,
                        textAlign: "left",
                        whiteSpace: "normal",
                      }}
                    >
                      The Invoice Quantity specified is more than quantity
                      available to invoice
                    </Typography.Text>
                  )}
                </>
              );
            },
          },
          {
            title: "Actual Unit Price (Incl. VAT)",
            width: 180,
            align: "right" as const,
            render: (_: unknown, r: SelectedLineItem) => (
              <InputNumber
                value={r.actualUnitPrice}
                disabled={!r.selected}
                size="small"
                style={{ width: "100%" }}
                onChange={(v) => changeUnitPrice(r.purchaseOrderLineItemId, v)}
              />
            ),
          },
          {
            title: "Actual Amount (Incl. VAT)",
            width: 180,
            align: "right" as const,
            render: (_: unknown, r: SelectedLineItem) =>
              r.selected || r.fullyInvoiced ? fmt(r.actualAmount) : "-",
          },
          {
            title: "Variance (Incl. VAT)",
            width: 150,
            align: "right" as const,
            render: (_: unknown, r: SelectedLineItem) => {
              if (!r.selected && !r.fullyInvoiced) return "-";
              const c =
                r.variance < 0 ? "red" : r.variance > 0 ? "green" : undefined;
              return <span style={{ color: c }}>{fmt(r.variance)}</span>;
            },
          },
        ]}
      />
      {/* Render the total outside the horizontally-scrollable Table so it's
          always visible regardless of column width. */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "baseline",
          gap: 12,
          padding: "12px 16px",
          marginTop: 8,
          borderTop: "1px solid #f0f0f0",
          background: "#fafafa",
        }}
      >
        <Typography.Text strong>Invoice Total (Incl. VAT):</Typography.Text>
        <Typography.Text strong style={{ fontSize: 16 }}>
          R {fmt(total)}
        </Typography.Text>
      </div>
    </Spin>
  );
};

/* ------------------------------------------------------------------ */
/*  Data-fetching wrapper                                              */
/* ------------------------------------------------------------------ */

const LINE_ITEMS_FIELD = "invoiceLineItems";

const InvoiceLineItemsLive: FC<{
  purchaseOrderId?: string;
}> = ({ purchaseOrderId }) => {
  const { backendUrl, httpHeaders } = useSheshaApplication();

  const { data } = useFormData();
  const form = useForm(false);
  const { globalState, setState: setGlobalState } = useGlobalState();
  const [items, setItems] = useState<AvailableLineItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Setting value (evaluated as a Mustache template against form data and
  // global state) takes priority, then fall back to form data's order.id
  const evaluatedOrderId = purchaseOrderId
    ? evaluateString(purchaseOrderId, { data, globalState })
    : "";
  const orderId = evaluatedOrderId || (data as any)?.order;

  useEffect(() => {
    // A Mustache template like {{data.order.id}} can briefly resolve to ""
    // mid-render while form data is in flux. Skip rather than clear so a
    // transient falsy orderId doesn't wipe already-loaded items.
    if (!orderId) return;

    let dead = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${backendUrl}/api/services/SupplierPortal/InvoiceLineItem/GetAvailableLineItems?orderId=${orderId}`,
          { headers: httpHeaders as unknown as Record<string, string> },
        );
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        if (!dead) setItems(json.result ?? []);
      } catch (err) {
        // Don't clear loaded items on transient errors — keep showing the
        // last good data instead of flashing back to the empty state.
        console.warn("Failed to load invoice line items", err);
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [orderId, backendUrl]);

  const formRef = useRef(form);
  formRef.current = form;
  const setGlobalStateRef = useRef(setGlobalState);
  setGlobalStateRef.current = setGlobalState;

  const handleSelectionChange = useCallback(
    (selectedRows: SelectedLineItem[]) => {
      formRef.current?.setFormData({
        values: { [LINE_ITEMS_FIELD]: selectedRows },
        mergeValues: true,
      });
      setGlobalStateRef.current({
        key: LINE_ITEMS_FIELD,
        data: selectedRows,
      });
    },
    [],
  );

  return (
    <LineItemsTable
      items={items}
      loading={loading}
      hasOrderId={Boolean(orderId)}
      onSelectionChange={handleSelectionChange}
    />
  );
};

/* ------------------------------------------------------------------ */
/*  Designer component definition                                      */
/* ------------------------------------------------------------------ */

export const InvoiceLineItemsComponent: IToolboxComponent<IProps> = {
  type: "invoiceLineItems",
  isInput: false,
  isOutput: false,
  name: "Invoice Line Items",
  icon: <UnorderedListOutlined />,
  Factory: ({ model }) => {
    if (model.hidden) return null;
    return (
      <InvoiceLineItemsLive
        purchaseOrderId={(model as IProps).purchaseOrderId}
      />
    );
  },
  settingsFormMarkup: settingsMarkup,
  validateSettings: (model) =>
    validateConfigurableComponentSettings(settingsMarkup, model),
};
